import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Globe2, ShieldCheck, Stethoscope } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { IMAGES } from "@/data/content";
import { useLang } from "@/i18n/LangContext";

const VALUE_ICONS = [Stethoscope, Compass, ShieldCheck, Globe2];

const TEAM = [
    { initials: "AO", name: "Dr. Amina Odhiambo" },
    { initials: "BM", name: "Brian Mwangi" },
    { initials: "GU", name: "Grace Uwase" },
    { initials: "DM", name: "Daniel Mushi" },
];

const About = () => {
    const { t, lang } = useLang();
    const a = t.about;

    useEffect(() => {
        document.title = lang === "sw" ? "Kuhusu Sisi — Hallomedics" : "About — Hallomedics";
    }, [lang]);

    return (
        <div className="pt-28 lg:pt-36" data-testid="about-page">
            <header className="mx-auto max-w-7xl px-5 sm:px-8">
                <p className="eyebrow flex items-center gap-2.5" data-testid="about-eyebrow">
                    <span className="inline-block h-2 w-2 rounded-full bg-teal animate-pulse-dot" />
                    {a.eyebrow}
                </p>
                <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl" data-testid="about-heading" key={lang}>
                    <MaskedLine delay={0.1}>{a.h1a}</MaskedLine>
                    <MaskedLine delay={0.22}>
                        <span className="text-primary dark:text-teal">{a.h1b}</span>
                    </MaskedLine>
                </h1>
            </header>

            <section className="mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-8 lg:grid-cols-12 lg:py-24" data-testid="about-mission">
                <div className="lg:col-span-6">
                    <Reveal>
                        <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">{a.p1}</p>
                        <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">{a.p2}</p>
                        <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">{a.p3}</p>
                    </Reveal>
                </div>
                <div className="lg:col-span-6">
                    <Reveal delay={0.12}>
                        <div className="group relative overflow-hidden rounded-md border border-border">
                            <img src={IMAGES.studentLearn} alt="Student using a laptop for e-learning" className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                            <div className="absolute bottom-4 left-4 rounded-full bg-background/85 px-4 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground backdrop-blur">
                                {a.imgChip}
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <Marquee />

            <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-28" data-testid="about-values">
                <Reveal>
                    <p className="eyebrow">{a.valuesEyebrow}</p>
                    <h2 className="mt-4 max-w-2xl font-heading text-2xl font-bold leading-snug tracking-tight sm:text-3xl lg:text-4xl">
                        {a.valuesH2}
                    </h2>
                </Reveal>
                <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                    {a.values.map((v, i) => {
                        const Icon = VALUE_ICONS[i];
                        return (
                            <Reveal key={v.title} delay={i * 0.08}>
                                <div className="h-full rounded-md border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-18px_rgba(15,76,58,0.35)]" data-testid={`value-card-${i}`}>
                                    <Icon className="h-6 w-6 text-terracotta" />
                                    <h3 className="mt-4 font-heading text-lg font-semibold tracking-tight">{v.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{v.copy}</p>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            <section className="border-t border-border bg-secondary/40" data-testid="about-team">
                <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-28">
                    <Reveal>
                        <p className="eyebrow">{a.teamEyebrow}</p>
                        <h2 className="mt-4 font-heading text-2xl font-bold leading-snug tracking-tight sm:text-3xl lg:text-4xl">
                            {a.teamH2}
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {TEAM.map((tm, i) => (
                            <Reveal key={tm.name} delay={i * 0.08}>
                                <div className="h-full rounded-md border border-border bg-card p-6" data-testid={`team-card-${i}`}>
                                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary font-heading text-lg font-bold text-primary-foreground">
                                        {tm.initials}
                                    </div>
                                    <h3 className="mt-5 font-heading text-lg font-semibold tracking-tight">{tm.name}</h3>
                                    <p className="mt-1 text-sm text-muted-foreground">{a.teamRoles[i]}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.3}>
                        <div className="mt-14">
                            <Link
                                to="/contact"
                                data-testid="about-cta-button"
                                className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
                            >
                                {a.cta}
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
};

export default About;
